import React, { createContext, useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { gtagEvent } from 'src/utils/gtag';
import useRestApiClient from 'src/hooks/useRestApiClient';
import { filterListBy } from 'src/utils/helpers/filter';

const initialState = {
  filteredTemplates: [],
  isLoading: false,
  isPreloaded: false,
  filterValues: { category: '', industry: '', search_title: '', sortby: '' },
  setFilterValues: () => Promise.resolve()
};

const TemplatesContext = createContext(initialState);

export const TemplatesProvider = (props) => {
  const { children, ...contextProviderProps } = props;
  const [filterValues, setFilterValues] = useState(initialState.filterValues);
  const [isPreloaded, setPreloaded] = useState(false);
  const [isLoading, setLoading] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [filteredTemplates, setFilteredTemplates] = useState([]);
  const client = useRestApiClient();

  const removeFromList = (templateId) => {
    setTemplates((prev) => {
      const filtered = prev.filter((row) => row.id !== templateId);
      return [...filtered];
    });
  };

  const loadTemplates = async (force = false) => {
    if (templates.length > 0 && !force) return;
    if (isLoading) return;

    try {
      setLoading(true);
      const {
        data: { message }
      } = await client.get('/templates');
      setTemplates(message);
      setFilteredTemplates(message);
      setPreloaded(true);
    } catch (error) {
      setTemplates([]);
      setFilteredTemplates([]);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const reloadTemplates = async () => {
    setLoading(true);
    const {
      data: { message }
    } = await client.get('/templates');
    setTemplates(message);
    setLoading(false);
  };

  useEffect(() => {
    let filterTemplates = templates;
    const {
      category,
      industry,
      sortby,
      search_title: searchTitle
    } = filterValues;

    filterTemplates = filterListBy(filterTemplates, 'category', category);
    filterTemplates = filterListBy(filterTemplates, 'industry', industry);

    if (sortby) {
      let sortedTemplates = [...filterTemplates];
      switch (sortby) {
        case 'latest':
          sortedTemplates = filterTemplates.sort((a, b) => {
            return new Date(b.created_at) - new Date(a.created_at);
          });
          break;
        case 'popular':
          sortedTemplates = filterTemplates.sort((a, b) => {
            const firstUsage = typeof a.usage !== 'undefined' ? a.usage : 0;
            const secondUsage = typeof b.usage !== 'undefined' ? b.usage : 0;
            return secondUsage - firstUsage;
          });
          break;
        default:
          // no default option
          break;
      }
      filterTemplates = [...sortedTemplates];
    }

    if (searchTitle && searchTitle.length >= 3) {
      gtagEvent('search', {
        search_term: searchTitle
      });

      filterTemplates = filterTemplates.filter(
        ({ description, title }) =>
          (description &&
            description.toLowerCase().indexOf(searchTitle.toLowerCase()) >
              -1) ||
          (title && title.toLowerCase().indexOf(searchTitle.toLowerCase()) > -1)
      );
    }

    setFilteredTemplates(filterTemplates);
  }, [filterValues, templates]);

  const values = useMemo(
    () => ({
      isPreloaded,
      isLoading,
      filterValues,
      templates,
      filteredTemplates,
      removeFromList,
      setFilterValues,
      loadTemplates,
      reloadTemplates
    }),
    [isPreloaded, isLoading, filterValues, templates, filteredTemplates]
  );

  return (
    <TemplatesContext.Provider value={values} {...contextProviderProps}>
      {children}
    </TemplatesContext.Provider>
  );
};

TemplatesProvider.propTypes = {
  children: PropTypes.node.isRequired
};

export default TemplatesContext;
